/**
 * Lens simulation — runs in HDR, *before* tone mapping.
 *
 * Radial distortion, transverse chromatic aberration and the ADS peripheral
 * defocus are all geometric: they resample the image, so they belong upstream of
 * the tone curve where the values are still scene-referred. The white flash goes
 * here too, added in HDR so ACES rolls it into a soft clipped white instead of a
 * flat paint-bucket fill.
 *
 * Everything is driven by r^4, which is ~0 across the middle of the frame and
 * only bites in the outer quarter. That is the whole trick to keeping this
 * invisible until you look for it.
 */
export const LensShader = {
  name: 'LensShader',

  defines: {
    ADS_TAPS: 6,
  },

  uniforms: {
    tDiffuse: { value: null },
    uAspect: { value: 1.777 },
    uDistortion: { value: 0.032 },
    uChroma: { value: 0.0032 },
    uAds: { value: 0.0 },
    uAdsRadius: { value: 0.011 },
    uFlash: { value: 0.0 },
    uDamage: { value: 0.0 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    varying vec2 vUv;

    uniform sampler2D tDiffuse;
    uniform float uAspect;
    uniform float uDistortion;
    uniform float uChroma;
    uniform float uAds;
    uniform float uAdsRadius;
    uniform float uFlash;
    uniform float uDamage;

    void main() {
      vec2 c = vUv - 0.5;

      // 0 at the centre, 1 at the corners.
      float r2 = clamp(dot(c, c) * 2.0, 0.0, 1.0);
      float edge = r2 * r2;              // r^4 falloff

      // Barrel distortion, renormalised so the corner maps exactly to the corner.
      // Without the 1/(1+k) the outermost pixels would sample off the texture and
      // smear against clamp-to-edge.
      float k = uDistortion;
      vec2 uv = 0.5 + c * ((1.0 + k * edge) / (1.0 + k));

      // Transverse CA: red pushed out, blue pulled in, along the radius.
      // Damage widens it — that is the "red fringe" of the hurt overlay.
      vec2 ca = c * (uChroma * (1.0 + uDamage * 2.5)) * edge;

      float blurR = uAds * edge * uAdsRadius;

      vec3 col;
      if (blurR > 2e-5) {
        // Peripheral defocus while aiming: a small rotated hexagon of taps.
        // The rotation is driven by radius so the ring never lines up into a
        // visible polygon.
        vec3 acc = vec3(
          texture2D(tDiffuse, uv + ca).r,
          texture2D(tDiffuse, uv).g,
          texture2D(tDiffuse, uv - ca).b
        );
        float wsum = 1.0;
        float phase = r2 * 5.7 + atan(c.y, c.x);
        for (int i = 0; i < ADS_TAPS; i++) {
          float a = phase + float(i) * 1.0471975512;
          vec2 o = vec2(cos(a), sin(a) * uAspect) * blurR;
          acc += vec3(
            texture2D(tDiffuse, uv + o + ca).r,
            texture2D(tDiffuse, uv + o).g,
            texture2D(tDiffuse, uv + o - ca).b
          );
          wsum += 1.0;
        }
        col = acc / wsum;
      } else {
        col = vec3(
          texture2D(tDiffuse, uv + ca).r,
          texture2D(tDiffuse, uv).g,
          texture2D(tDiffuse, uv - ca).b
        );
      }

      // Flashbang / explosion. Added in scene-referred space, slightly warm.
      col += uFlash * uFlash * vec3(6.0, 5.85, 5.55);

      gl_FragColor = vec4(col, 1.0);
    }
  `,
};
